import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, MapPin, MessageSquareText, ServerCog, Sparkles, CheckCircle2, Lock, Bell } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

const PRODUCTS = [
    {
        id: "local-seo",
        kicker: "01 · SEO Local",
        name: "Local SEO",
        tagline: "Dominez la Fiche Google de vos clients.",
        desc: "Centralisez toutes les fiches Google Business Profile / My Business de vos clients dans une seule app. Publiez, programmez, surveillez et boostez l'algorithme local sans jongler entre 50 onglets.",
        features: [
            "Gestion multi-établissements & multi-clients",
            "Posts Google automatisés & programmés",
            "Surveillance des avis en temps réel",
            "Tarif clair, sans engagement",
        ],
        accent: "217 91% 60%",
        accent2: "152 76% 52%",
        url: "https://local-seo.site/",
        image: "/products/local-seo.jpg",
        Icon: MapPin,
        price: "100 € HT / mois",
        badge: "Best-seller",
    },
    {
        id: "sms-avis",
        kicker: "02 · Avis & E-réputation",
        name: "SMS Avis",
        tagline: "Collectez 10× plus d'avis Google.",
        desc: "Envoyez à vos clients des invitations à laisser un avis Google par SMS ou QR code. Une interface limpide pour les pros, une expérience express pour leurs clients. Plus d'avis, plus d'étoiles, plus de business.",
        features: [
            "Invitations SMS & QR codes personnalisés",
            "Marque employeur intégrée",
            "Reporting clair par établissement",
            "Conformité RGPD & opt-in propre",
        ],
        accent: "330 81% 60%",
        accent2: "40 90% 55%",
        url: "https://sms-avis.fr/",
        image: "/products/sms-avis.jpg",
        Icon: MessageSquareText,
        price: "À partir de 49 € HT / mois",
        badge: "+38% d'avis",
    },
    {
        id: "plesk-dash",
        kicker: "03 · Hébergement & Ops",
        name: "PleskDash",
        tagline: "Fini le jeu de piste entre vos serveurs.",
        desc: "Un dashboard unifié pour piloter tous vos serveurs Plesk depuis une seule interface. Connectez-vous à n'importe quel site en 1 clic. Retrouvez les bons clients en 1 seconde. La fin du chaos serveur.",
        features: [
            "API en lecture seule — sécurisé",
            "Serveurs et utilisateurs illimités",
            "Recherche instantanée multi-serveurs",
            "Onboarding en moins de 5 minutes",
        ],
        accent: "199 89% 55%",
        accent2: "217 91% 60%",
        url: "https://pleskdash.top/",
        image: "/products/pleskdash.jpg",
        Icon: ServerCog,
        price: "Sur devis",
        badge: "Conçu pour pros",
    },
];

export default function ProductsSection() {
    return (
        <section id="solutions" className="relative py-24 sm:py-32">
            <div className="mx-auto max-w-7xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <Badge variant="outline" className="rounded-full border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground">
                        La suite de solutions
                    </Badge>
                    <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                        Trois SaaS. <span className="text-gradient">Zéro fioriture.</span><br />
                        Tous les leviers d'une agence moderne.
                    </h2>
                    <p className="mt-5 text-base text-muted-foreground sm:text-lg">
                        Pensés pour être adoptés en une après-midi. Optimisés pour générer du chiffre d'affaires dès la première semaine.
                    </p>
                </div>

                <div className="mt-16 space-y-10">
                    {PRODUCTS.map((p, i) => (
                        <ProductRow key={p.id} product={p} reverse={i % 2 === 1} />
                    ))}
                </div>

                {/* Bientôt */}
                <ComingSoonCard />
            </div>
        </section>
    );
}

const ProductRow = ({ product, reverse }) => {
    const { kicker, name, tagline, desc, features, accent, accent2, url, image, Icon, price, badge } = product;

    const handleSubscribe = (e) => {
        e.preventDefault();
        toast.success(`Redirection vers la souscription ${name}…`, {
            description: `Vous allez être redirigé vers ${url}`,
        });
        setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), 800);
    };

    return (
        <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="group relative overflow-hidden rounded-3xl border border-border bg-gradient-card p-1"
        >
            <div
                className="absolute inset-x-0 top-0 h-px"
                style={{ background: `linear-gradient(90deg, transparent, hsl(${accent}), hsl(${accent2}), transparent)` }}
            />
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-20 blur-3xl transition-opacity duration-700 group-hover:opacity-30" style={{ background: `radial-gradient(circle, hsl(${accent}), transparent 70%)` }} />

            <div className={`grid items-stretch gap-0 rounded-[calc(1.5rem-4px)] bg-background/40 lg:grid-cols-2 ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
                {/* Content */}
                <div className="flex flex-col p-7 sm:p-10">
                    <div className="flex items-center justify-between gap-3">
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em]" style={{ color: `hsl(${accent})` }}>
                            {kicker}
                        </span>
                        <Badge variant="outline" className="rounded-full border-border bg-card/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            {badge}
                        </Badge>
                    </div>

                    <div className="mt-5 flex items-center gap-3">
                        <div
                            className="flex h-11 w-11 items-center justify-center rounded-xl"
                            style={{ background: `linear-gradient(135deg, hsl(${accent} / 0.25), hsl(${accent2} / 0.15))`, border: `1px solid hsl(${accent} / 0.3)` }}
                        >
                            <Icon className="h-5 w-5" style={{ color: `hsl(${accent})` }} />
                        </div>
                        <h3 className="font-display text-3xl font-semibold text-foreground sm:text-4xl">{name}</h3>
                    </div>

                    <p className="mt-4 font-display text-lg font-medium text-foreground/90 sm:text-xl">
                        {tagline}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                        {desc}
                    </p>

                    <ul className="mt-6 grid gap-2.5">
                        {features.map((f) => (
                            <li key={f} className="flex items-start gap-2.5 text-sm text-foreground/90">
                                <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: `hsl(${accent})` }} />
                                <span>{f}</span>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-8">
                        <div>
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Tarif</div>
                            <div className="font-display text-lg font-semibold text-foreground">{price}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button asChild variant="outline" size="sm" className="rounded-lg border-border bg-card/40 hover:bg-card">
                                <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
                                    Voir le site <ArrowUpRight className="h-3.5 w-3.5" />
                                </a>
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleSubscribe}
                                className="rounded-lg text-primary-foreground shadow-glow"
                                style={{ background: `linear-gradient(135deg, hsl(${accent}), hsl(${accent2}))` }}
                            >
                                Souscrire en ligne
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Visual */}
                <div className="relative min-h-[320px] overflow-hidden border-t border-border lg:border-l lg:border-t-0">
                    <div
                        className="absolute inset-0"
                        style={{ background: `radial-gradient(circle at 30% 20%, hsl(${accent} / 0.25), transparent 60%), radial-gradient(circle at 80% 80%, hsl(${accent2} / 0.2), transparent 60%)` }}
                    />
                    <div className="relative flex h-full items-center justify-center p-8">
                        <div className="relative w-full max-w-md">
                            <div
                                className="absolute -inset-3 rounded-2xl blur-2xl opacity-50"
                                style={{ background: `linear-gradient(135deg, hsl(${accent} / 0.5), hsl(${accent2} / 0.5))` }}
                            />
                            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-elegant">
                                <div className="flex items-center gap-1.5 border-b border-border bg-background/60 px-3 py-2">
                                    <span className="h-2 w-2 rounded-full bg-destructive/60" />
                                    <span className="h-2 w-2 rounded-full bg-[hsl(40_90%_55%)]" />
                                    <span className="h-2 w-2 rounded-full bg-success/60" />
                                    <span className="ml-2 font-mono text-[9px] text-muted-foreground">{url.replace('https://', '')}</span>
                                </div>
                                <img
                                    src={image}
                                    alt={`${name} preview`}
                                    className="h-auto w-full transition-transform duration-700 group-hover:scale-[1.03]"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

const ComingSoonCard = () => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="mt-10 overflow-hidden rounded-3xl border border-dashed border-border bg-card/30 p-1"
    >
        <div className="flex flex-col items-center gap-6 rounded-[calc(1.5rem-4px)] bg-background/40 p-8 text-center sm:p-12 lg:flex-row lg:text-left">
            <div className="relative">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-primary opacity-50 blur-sm" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="h-7 w-7 text-primary animate-pulse-glow" />
                </div>
            </div>
            <div className="flex-1">
                <div className="flex flex-wrap items-center justify-center gap-2 lg:justify-start">
                    <Badge className="rounded-full bg-accent/15 text-accent ring-1 ring-accent/30 hover:bg-accent/15">
                        <Bell className="mr-1 h-3 w-3" /> Bientôt disponible
                    </Badge>
                    <Badge variant="outline" className="rounded-full border-border">04, 05, 06…</Badge>
                </div>
                <h3 className="mt-3 font-display text-2xl font-semibold text-foreground sm:text-3xl">
                    De nouveaux SaaS rejoignent la suite chaque trimestre.
                </h3>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                    Outils de facturation, signature de devis, audit web express, automatisations… Tous testés en interne avant d'arriver dans vos mains.
                </p>
            </div>
            <Button size="lg" className="rounded-xl bg-gradient-primary text-primary-foreground shadow-glow whitespace-nowrap" asChild>
                <a href="#cta" className="inline-flex items-center gap-2">
                    <Lock className="h-4 w-4" /> Être prévenu
                </a>
            </Button>
        </div>
    </motion.div>
);
